import { useState } from 'react';
import type { JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../lib/api.js';
import { BrandSelect } from '../lib/brands.js';
import { dateTime, num, pct } from '../lib/format.js';
import type { Me, Rights } from '../lib/session.js';
import {
  Bar,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  PageHeader,
  Panel,
  StatusBadge,
  TabBar,
} from '../components/ui.js';
import type { IconName, Tone } from '../components/ui.js';
import { SitesImport } from './SitesImport.js';
import '../screens-2a.css';
import '../screens-web.css';

/**
 * Chování na webu: co lidé dělají po e-mailu (funnel), které zprávy je na web přivedly
 * a jestli weby posílají data. Čísla jsou z měření `hub.js` po souhlasu návštěvníka, takže
 * ukazují jen lidi, kteří souhlas dali.
 */

interface Step {
  key: string;
  label: string;
  count: number;
}
interface CodeRow {
  code: string;
  sent: number;
  clicked: number;
  visited: number;
  active: number;
  medianEngagedSeconds: number;
}
type State = 'off' | 'waiting' | 'ok' | 'silent';
interface SiteRow {
  id: string;
  name: string;
  seller: string;
  enabled: boolean;
  lastEventAt: string | null;
  visits24h: number;
  visits7d: number;
  activeShare: number | null;
  attributedShare: number | null;
  rejectedOrigin7d: number;
  state: State;
  coverage: number | null;
}
interface SitesData {
  items: SiteRow[];
  summary: { total: number; ok: number; silent: number; waiting: number; off: number };
}

type Tab = 'overview' | 'messages' | 'sites' | 'ga4';
const TABS: Array<{ id: Tab; label: string; hint: string; icon: IconName }> = [
  { id: 'overview', label: 'Přehled', hint: 'Od odeslání po nákup', icon: 'chart' },
  { id: 'messages', label: 'Zprávy', hint: 'Které zprávy vedou na web', icon: 'mail' },
  { id: 'sites', label: 'Weby', hint: 'Posílají weby data?', icon: 'plug' },
  { id: 'ga4', label: 'E-mail vs. GA4', hint: 'Měření hubu vedle Google Analytics', icon: 'chart' },
];

const STATE: Record<State, { label: string; tone: Tone }> = {
  ok: { label: 'V pořádku', tone: 'ok' },
  waiting: { label: 'Čeká na první data', tone: 'warn' },
  silent: { label: 'Mlčí', tone: 'bad' },
  off: { label: 'Vypnutý', tone: 'neu' },
};

const PERIODS = [
  { days: 7, label: 'Posledních 7 dní' },
  { days: 30, label: 'Posledních 30 dní' },
  { days: 90, label: 'Posledních 90 dní' },
];

const dayOf = (d: Date): string => d.toISOString().slice(0, 10);

/** Období do dne hubu (`me.now`), ne podle hodin počítače. */
function periodQuery(now: string, days: number): { from: string; to: string } {
  const to = new Date(now);
  return { from: dayOf(new Date(to.getTime() - (days - 1) * 86_400_000)), to: dayOf(to) };
}

const seconds = (s: number): string =>
  s >= 60 ? `${Math.floor(s / 60)} min ${s % 60} s` : `${s} s`;

function Filters({
  seller,
  onSeller,
  days,
  onDays,
  children,
}: {
  seller: string;
  onSeller: (v: string) => void;
  days?: number;
  onDays?: (v: number) => void;
  children?: JSX.Element;
}): JSX.Element {
  return (
    <div className="s2a-filters">
      <Field label="Značka">
        <BrandSelect value={seller} onChange={onSeller} />
      </Field>
      {days !== undefined && onDays ? (
        <Field label="Období">
          <select value={days} onChange={(e) => onDays(Number(e.target.value))}>
            {PERIODS.map((p) => (
              <option key={p.days} value={p.days}>
                {p.label}
              </option>
            ))}
          </select>
        </Field>
      ) : null}
      {children}
    </div>
  );
}

function Overview({ me }: { me: Me }): JSX.Element {
  const [seller, setSeller] = useState('');
  const [days, setDays] = useState(30);
  const [code, setCode] = useState('');
  const period = periodQuery(me.now, days);
  const query = useQuery<{ steps: Step[] }>({
    queryKey: ['web-funnel', seller, days, code, period.to],
    queryFn: () => api.get(`/api/web/funnel${qs({ ...period, seller, code })}`),
  });
  const steps = query.data?.steps ?? [];
  const top = steps[0]?.count ?? 0;

  return (
    <>
      <Filters seller={seller} onSeller={setSeller} days={days} onDays={setDays}>
        <Field label="Kód zprávy" hint="Například S06 nebo M01; prázdné = všechny.">
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.trim().toUpperCase())}
            maxLength={20}
          />
        </Field>
      </Filters>
      <Panel
        title="Od odeslání po nákup"
        subtitle="Počítá se po zprávách, ne po lidech. Návštěvu webu vidíme jen u lidí, kteří dali souhlas s měřením. Nákup je zaplacená objednávka do 24 hodin po návštěvě ze zprávy."
      >
        {query.isLoading ? (
          <LoadingState what="přehled" />
        ) : query.isError ? (
          <ErrorState
            title="Přehled se nepodařilo načíst"
            error={query.error}
            onRetry={() => void query.refetch()}
          />
        ) : top === 0 ? (
          <EmptyState title="V tomto období nebyla odeslána žádná zpráva">
            Zvolte delší období nebo jinou značku. Zprávy nanečisto se nepočítají.
          </EmptyState>
        ) : (
          <div className="scroll">
            <table data-testid="web-funnel">
              <thead>
                <tr>
                  <th>Krok</th>
                  <th className="n">Zpráv</th>
                  <th>Z odeslaných</th>
                  <th className="n">Z předchozího kroku</th>
                </tr>
              </thead>
              <tbody>
                {steps.map((s, i) => {
                  const before = steps[i - 1]?.count ?? 0;
                  return (
                    <tr key={s.key}>
                      <td>{s.label}</td>
                      <td className="n">{num(s.count)}</td>
                      <td>
                        <div className="web-share">
                          <Bar value={s.count} max={top} />
                          <span>{pct(Math.round((s.count / top) * 100))}</span>
                        </div>
                      </td>
                      <td className="n">
                        {i === 0 || before === 0 ? '—' : pct(Math.round((s.count / before) * 100))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </>
  );
}

function Messages({ me }: { me: Me }): JSX.Element {
  const [seller, setSeller] = useState('');
  const [days, setDays] = useState(30);
  const period = periodQuery(me.now, days);
  const query = useQuery<{ items: CodeRow[] }>({
    queryKey: ['web-messages', seller, days, period.to],
    queryFn: () => api.get(`/api/web/messages${qs({ ...period, seller })}`),
  });
  const items = query.data?.items ?? [];

  return (
    <>
      <Filters seller={seller} onSeller={setSeller} days={days} onDays={setDays} />
      <Panel
        title="Podle kódu zprávy"
        subtitle="Kolik odeslaných zpráv vedlo na web, kolik z toho byly aktivní návštěvy a jak dlouho lidé na webu skutečně byli (medián aktivního času)."
      >
        {query.isLoading ? (
          <LoadingState what="zprávy" />
        ) : query.isError ? (
          <ErrorState
            title="Zprávy se nepodařilo načíst"
            error={query.error}
            onRetry={() => void query.refetch()}
          />
        ) : items.length === 0 ? (
          <EmptyState title="V tomto období nebyla odeslána žádná zpráva">
            Zvolte delší období nebo jinou značku.
          </EmptyState>
        ) : (
          <div className="scroll">
            <table data-testid="web-messages">
              <thead>
                <tr>
                  <th>Kód zprávy</th>
                  <th className="n">Odesláno</th>
                  <th className="n">Kliknuto</th>
                  <th className="n">Přišlo na web</th>
                  <th className="n">Aktivních návštěv</th>
                  <th className="n">Medián aktivního času</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.code}>
                    <td>
                      <code>{r.code}</code>
                    </td>
                    <td className="n">{num(r.sent)}</td>
                    <td className="n">{num(r.clicked)}</td>
                    <td className="n">{num(r.visited)}</td>
                    <td className="n">{num(r.active)}</td>
                    <td className="n">{r.visited > 0 ? seconds(r.medianEngagedSeconds) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </>
  );
}

const KC = new Intl.NumberFormat('cs-CZ', { maximumFractionDigits: 0 });

interface Ga4Row {
  host: string;
  site: string | null;
  seller: string | null;
  emailClicks: number | null;
  emailSessions: number;
  emailEngagedSessions: number;
  emailKeyEvents: number;
  emailTransactions: number;
  emailRevenue: number;
  allSessions: number;
  hubVisits: number;
  coverage: number | null;
}
interface Ga4Data {
  from: string;
  to: string;
  items: Ga4Row[];
  totals: Omit<Ga4Row, 'host' | 'site' | 'seller'>;
  summary: { lastPulledAt: string | null; ga4Enabled: boolean };
}

/** Čísla z hubu (kliky, návštěvy `hub.js`) a z GA4 (sessions) vedle sebe; nesčítají se (G9). */
function Ga4Compare({ me }: { me: Me }): JSX.Element {
  const [seller, setSeller] = useState('');
  const [days, setDays] = useState(7);
  const period = periodQuery(me.now, days);
  const query = useQuery<Ga4Data>({
    queryKey: ['web-ga4', seller, days, period.to],
    queryFn: () => api.get(`/api/web/ga4${qs({ ...period, seller })}`),
  });
  const data = query.data;

  return (
    <>
      <Filters seller={seller} onSeller={setSeller} days={days} onDays={setDays} />
      <Panel
        title="E-mail vs. Google Analytics 4"
        subtitle="Po webech: kolik lidí kliklo v e-mailu (hub), kolik návštěv z e-mailu eviduje GA4 a kolik z nich vidí náš skript hub.js (pokrytí). Zdroje se liší, proto se nesčítají."
      >
        {query.isLoading ? (
          <LoadingState what="srovnání s GA4" />
        ) : query.isError ? (
          <ErrorState
            title="Srovnání se nepodařilo načíst"
            error={query.error}
            onRetry={() => void query.refetch()}
          />
        ) : !data || data.items.length === 0 ? (
          <EmptyState title="Zatím nejsou data z GA4">
            {data?.summary.ga4Enabled
              ? 'Stahování je zapnuté, první data přijdou po nejbližším běhu úlohy.'
              : 'Stahování z Google Analytics 4 je vypnuté nebo nenastavené. Zapíná se v Nastavení → Napojení.'}
          </EmptyState>
        ) : (
          <>
            <p className="note" data-testid="web-ga4-fresh">
              Data GA4 aktuální k{' '}
              {data.summary.lastPulledAt ? dateTime(data.summary.lastPulledAt) : 'neznámo'}. GA4
              data dopočítává až 48 hodin zpětně. Kliknutí není návštěva: část lidí web neotevře,
              část neudělí souhlas s měřením. Kliky jsou za značku webu.
            </p>
            <div className="scroll">
              <table data-testid="web-ga4">
                <thead>
                  <tr>
                    <th>Web</th>
                    <th>Značka</th>
                    <th className="n">Kliky z e-mailu (hub)</th>
                    <th className="n">Sessions z e-mailu (GA4)</th>
                    <th className="n">Zapojené sessions (GA4)</th>
                    <th className="n">Klíčové události (GA4)</th>
                    <th className="n">Transakce (GA4)</th>
                    <th className="n">Tržby (GA4)</th>
                    <th className="n">Sessions celkem (GA4)</th>
                    <th className="n">Návštěvy hub.js (hub)</th>
                    <th className="n">Pokrytí hub.js</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((r) => (
                    <tr key={r.host}>
                      <td>
                        {r.site ?? <em>bez webu v hubu</em>}
                        <div className="note">{r.host || '(bez názvu hostitele)'}</div>
                      </td>
                      <td>{r.seller ?? '—'}</td>
                      <td className="n">{num(r.emailClicks)}</td>
                      <td className="n">{num(r.emailSessions)}</td>
                      <td className="n">{num(r.emailEngagedSessions)}</td>
                      <td className="n">{num(r.emailKeyEvents)}</td>
                      <td className="n">{num(r.emailTransactions)}</td>
                      <td className="n">{KC.format(r.emailRevenue)}</td>
                      <td className="n">{num(r.allSessions)}</td>
                      <td className="n">{r.site ? num(r.hubVisits) : '—'}</td>
                      <td className="n">
                        {r.coverage === null ? '—' : pct(Math.round(r.coverage * 100))}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={2}>
                      <b>Celkem</b>
                    </td>
                    <td className="n">{num(data.totals.emailClicks)}</td>
                    <td className="n">{num(data.totals.emailSessions)}</td>
                    <td className="n">{num(data.totals.emailEngagedSessions)}</td>
                    <td className="n">{num(data.totals.emailKeyEvents)}</td>
                    <td className="n">{num(data.totals.emailTransactions)}</td>
                    <td className="n">{KC.format(data.totals.emailRevenue)}</td>
                    <td className="n">{num(data.totals.allSessions)}</td>
                    <td className="n">{num(data.totals.hubVisits)}</td>
                    <td className="n">
                      {data.totals.coverage === null
                        ? '—'
                        : pct(Math.round(data.totals.coverage * 100))}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )}
      </Panel>
    </>
  );
}

function Sites({ rights }: { rights: Rights }): JSX.Element {
  const client = useQueryClient();
  const [seller, setSeller] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const canManage = rights.can('manage_users');
  const query = useQuery<SitesData>({
    queryKey: ['web-sites', seller],
    queryFn: () => api.get(`/api/web/sites${qs({ seller })}`),
  });
  const bulk = useMutation({
    mutationFn: (enabled: boolean) => api.post('/api/sites/bulk', { ids: selected, enabled }),
    onSuccess: () => {
      setSelected([]);
      void client.invalidateQueries({ queryKey: ['web-sites'] });
    },
  });
  const items = query.data?.items ?? [];
  const s = query.data?.summary;
  const toggle = (id: string): void =>
    setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  return (
    <>
      <Filters seller={seller} onSeller={setSeller} />
      <Panel
        title="Weby"
        subtitle="Jestli weby posílají data, kolik mají návštěv a kolik událostí hub odmítl kvůli cizí doméně."
      >
        {query.isLoading ? (
          <LoadingState what="weby" />
        ) : query.isError ? (
          <ErrorState
            title="Weby se nepodařilo načíst"
            error={query.error}
            onRetry={() => void query.refetch()}
          />
        ) : items.length === 0 ? (
          <EmptyState title="Zatím tu není žádný web">
            {canManage
              ? 'Založte je níže hromadně ze souboru, nebo po jednom v Nastavení, v kartě Napojení.'
              : 'Weby zakládá správce v Nastavení, v kartě Napojení.'}
          </EmptyState>
        ) : (
          <>
            <p className="s2a-count" data-testid="web-sites-summary">
              {num(s?.total)} webů: {num(s?.ok)} v pořádku, {num(s?.silent)} mlčí, {num(s?.waiting)}{' '}
              čeká na první data, {num(s?.off)} vypnuto.
            </p>
            {canManage ? (
              <div className="s2a-filters">
                <button
                  type="button"
                  className="btn"
                  disabled={selected.length === 0 || bulk.isPending}
                  onClick={() => bulk.mutate(true)}
                >
                  Zapnout vybrané
                </button>
                <button
                  type="button"
                  className="btn"
                  disabled={selected.length === 0 || bulk.isPending}
                  onClick={() => bulk.mutate(false)}
                >
                  Vypnout vybrané
                </button>
                {bulk.isError ? <ErrorState title="Změna se nepovedla" error={bulk.error} /> : null}
              </div>
            ) : null}
            <div className="scroll">
              <table data-testid="web-sites">
                <thead>
                  <tr>
                    {canManage ? (
                      <th>
                        <span className="sr-only">Výběr</span>
                      </th>
                    ) : null}
                    <th>Web</th>
                    <th>Značka</th>
                    <th>Stav</th>
                    <th>Poslední událost</th>
                    <th className="n">Návštěv za 24 h / 7 dní</th>
                    <th className="n">Aktivních</th>
                    <th className="n">Ze zprávy</th>
                    <th className="n">Odmítnuto za 7 dní</th>
                    <th className="n">Pokrytí podle GA4</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => {
                    const st = STATE[r.state];
                    return (
                      <tr key={r.id}>
                        {canManage ? (
                          <td>
                            <input
                              type="checkbox"
                              aria-label={`Vybrat web ${r.name}`}
                              checked={selected.includes(r.id)}
                              onChange={() => toggle(r.id)}
                            />
                          </td>
                        ) : null}
                        <td>{r.name}</td>
                        <td>{r.seller}</td>
                        <td>
                          <StatusBadge tone={st.tone}>{st.label}</StatusBadge>
                        </td>
                        <td>{dateTime(r.lastEventAt)}</td>
                        <td className="n">
                          {num(r.visits24h)} / {num(r.visits7d)}
                        </td>
                        <td className="n">
                          {r.activeShare === null ? '—' : pct(Math.round(r.activeShare * 100))}
                        </td>
                        <td className="n">
                          {r.attributedShare === null
                            ? '—'
                            : pct(Math.round(r.attributedShare * 100))}
                        </td>
                        <td className="n">{num(r.rejectedOrigin7d)}</td>
                        <td className="n">
                          {r.coverage === null ? '—' : pct(Math.round(r.coverage * 100))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Panel>
      {canManage ? <SitesImport /> : null}
    </>
  );
}

export function WebBehavior({ me, rights }: { me: Me; rights: Rights }): JSX.Element {
  const [tab, setTab] = useState<Tab>('overview');
  return (
    <>
      <PageHeader
        title="Chování na webu"
        description="Co lidé udělali na webu poté, co přišli z našeho e-mailu: kolik jich přišlo, jak dlouho byli skutečně aktivní a jestli koupili. Měří se jen u lidí, kteří dali na webu souhlas s měřením."
      />
      <TabBar tabs={TABS} value={tab} onChange={setTab} />
      {tab === 'overview' ? <Overview me={me} /> : null}
      {tab === 'messages' ? <Messages me={me} /> : null}
      {tab === 'sites' ? <Sites rights={rights} /> : null}
      {tab === 'ga4' ? <Ga4Compare me={me} /> : null}
    </>
  );
}
